import React, { useState } from 'react'
import styled from 'styled-components'
import { useParams, useNavigate } from 'react-router-dom'
import Text from 'core-system/Text/Text'
import Chip from 'core-system/Chip'
import palette from 'core-system/Themes/palette'
import { Locations } from 'shared/Router/Locations'
import { SiteTabType } from './siteDetailTypes'
import { mockSiteDetail, mockSitesByName } from './siteDetailMockData'
import {
  ProgramsTab,
  EmployeeSegmentsTab,
  ComplianceTab,
  ActivityTab,
} from './components/SiteDetail'

const Container = styled.div`
  padding: 2rem;
  max-width: 1200px;
  margin: 0 auto;
`

const Breadcrumb = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.75rem;
`

const BreadcrumbLink = styled.button`
  background: none;
  border: none;
  color: ${palette.primary.pink800};
  font-size: 0.875rem;
  cursor: pointer;
  padding: 0;

  &:hover {
    text-decoration: underline;
  }
`

const BreadcrumbSeparator = styled.span`
  color: ${palette.text.secondary};
  font-size: 0.875rem;
`

const BreadcrumbCurrent = styled.span`
  color: ${palette.text.secondary};
  font-size: 0.875rem;
`

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 1.5rem;
`

const HeaderLeft = styled.div`
  display: flex;
  flex-direction: column;
`

const SiteName = styled(Text)`
  font-weight: 600;
  margin-bottom: 0.25rem;
`

const SiteAddress = styled(Text)`
  color: ${palette.text.secondary};
  font-size: 0.875rem;
`

const HeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`

const EditButton = styled.button`
  background-color: ${palette.primary.pink800};
  color: white;
  padding: 0.5rem 1rem;
  font-size: 0.8125rem;
  font-weight: 500;
  border: none;
  border-radius: 0.375rem;
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: ${palette.primary.pink900};
  }
`

const StatsRow = styled.div`
  display: flex;
  gap: 2rem;
  margin-bottom: 1.5rem;
`

const StatItem = styled.div`
  display: flex;
  flex-direction: column;
`

const StatValue = styled(Text)`
  font-weight: 600;
  font-size: 1.5rem;
  line-height: 1.2;
`

const StatLabel = styled(Text)`
  color: ${palette.text.secondary};
  font-size: 0.75rem;
`

const TabsContainer = styled.div`
  display: flex;
  gap: 0;
  border-bottom: 1px solid ${palette.grey.grey200};
  margin-bottom: 0;
`

const Tab = styled.button<{ $isActive: boolean }>`
  padding: 0.75rem 1.25rem;
  background: none;
  border: none;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  color: ${({ $isActive }) =>
    $isActive ? palette.text.primary : palette.text.secondary};
  border-bottom: 2px solid
    ${({ $isActive }) => ($isActive ? palette.primary.pink800 : 'transparent')};
  margin-bottom: -1px;
  transition: color 0.2s ease;

  &:hover {
    color: ${palette.text.primary};
  }
`

const SiteDetailView = React.memo(() => {
  const { siteId } = useParams<{ siteId: string }>()
  const navigate = useNavigate()
  const [activeTab, setActiveTab] = useState<SiteTabType>('programs')

  const siteData = (siteId && mockSitesByName[siteId]) || mockSiteDetail

  const tabs: { id: SiteTabType; label: string }[] = [
    { id: 'programs', label: 'Programs' },
    { id: 'segments', label: 'Employee Segments' },
    { id: 'compliance', label: 'Compliance' },
    { id: 'activity', label: 'Activity' },
  ]

  const renderTabContent = () => {
    switch (activeTab) {
      case 'programs':
        return <ProgramsTab programs={siteData.programs} />
      case 'segments':
        return <EmployeeSegmentsTab data={siteData.segments} />
      case 'compliance':
        return (
          <ComplianceTab data={siteData.compliance} siteName={siteData.name} />
        )
      case 'activity':
        return <ActivityTab activities={siteData.activities} />
      default:
        return <ProgramsTab programs={siteData.programs} />
    }
  }

  return (
    <Container>
      <Breadcrumb>
        <BreadcrumbLink onClick={() => navigate(Locations.Dashboard)}>
          Dashboard
        </BreadcrumbLink>
        <BreadcrumbSeparator>/</BreadcrumbSeparator>
        <BreadcrumbCurrent>{siteData.name}</BreadcrumbCurrent>
      </Breadcrumb>

      <Header>
        <HeaderLeft>
          <SiteName variant='h2'>{siteData.name}</SiteName>
          <SiteAddress>{siteData.address}</SiteAddress>
        </HeaderLeft>
        <HeaderRight>
          <Chip
            variant={
              siteData.complianceStatus === 'Compliant'
                ? 'green'
                : siteData.complianceStatus === 'Action Needed'
                  ? 'red'
                  : 'grey'
            }
            width='auto'
          >
            {siteData.complianceStatus}
          </Chip>
          <EditButton>Edit Site</EditButton>
        </HeaderRight>
      </Header>

      <StatsRow>
        <StatItem>
          <StatValue>{siteData.employeeCount.toLocaleString()}</StatValue>
          <StatLabel>Employees</StatLabel>
        </StatItem>
        <StatItem>
          <StatValue>{siteData.enrolledCount.toLocaleString()}</StatValue>
          <StatLabel>Enrolled</StatLabel>
        </StatItem>
        <StatItem>
          <StatValue>{siteData.participationRate}%</StatValue>
          <StatLabel>Participation</StatLabel>
        </StatItem>
        <StatItem>
          <StatValue>{siteData.lastSync}</StatValue>
          <StatLabel>Last Sync</StatLabel>
        </StatItem>
      </StatsRow>

      <TabsContainer>
        {tabs.map((tab) => (
          <Tab
            key={tab.id}
            $isActive={activeTab === tab.id}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </Tab>
        ))}
      </TabsContainer>

      {renderTabContent()}
    </Container>
  )
})

if (process.env.NODE_ENV !== 'production') {
  SiteDetailView.displayName = 'SiteDetailView'
}

export default SiteDetailView
